import React, { useCallback, useMemo, useRef } from 'react';

import { doesFocusableExist, getCurrentFocusKey } from '@noriginmedia/norigin-spatial-navigation';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import ButtonGroup from '@smart-tv/components/button-group/button-group';
import Overlay from '@smart-tv/components/overlay/overlay';
import Typography from '@smart-tv/components/typography/typography';
import testIds from '@smart-tv/e2e/shared/variables';

import useAutoHide from '../_hooks/use-auto-hide';
import getAudioTrackDisplayText from '../_utils/format-audio-track';
import getSubtitleTrackDisplayText from '../_utils/format-subtitle-track';
import { AUTO_HIDE_TIMEOUT } from '../player-helpers';
import { AudioTrack, Player, SubtitleTrack } from '../types';
import usePlayerAudioDialog from './_hooks/use-player-audio-dialog';
import { NO_SUBTITLE_TRACK_ID } from './player-controls';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import { isBackKey } from '@smart-tv/utils/remoteKeys';
import cleanAudioTrackText from '@smart-tv/utils/formatter/clean-track-text';
import styles from './player-controls.module.scss';

interface Props {
  open: boolean;
  onClose: () => void;
  subtitleTracks?: SubtitleTrack[];
  selectedSubtitleTrack?: SubtitleTrack;
  setSelectedSubtitleTrack: (subtitleTrack: SubtitleTrack | undefined) => void;
  audioTracks?: AudioTrack[];
  selectedAudioTrack?: AudioTrack;
  setSelectedAudioTrack: (audioTrack: AudioTrack | undefined) => void;
  player?: Player;
}

export const PlayerSettingsOverlay = (props: Props) => {
  const {
    open,
    onClose,
    subtitleTracks,
    selectedSubtitleTrack,
    setSelectedSubtitleTrack,
    player,
    audioTracks,
    selectedAudioTrack
  } = props;


  const { handleSelectAudioTrack } = usePlayerAudioDialog(props);

  const {
    handleKeyDown: registerKeyDown,
    handleKeyUp: registerKeyUp,
    handleMouseOver,
    handleMouseLeave
  } = useAutoHide({
    enabled: open,
    timeout: AUTO_HIDE_TIMEOUT,
    onHide: onClose
  });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      registerKeyDown();

      if (isBackKey(event)) {
        event.stopPropagation();
        onClose();
        return;
      }

      // if we dont currently have a valid focus key, dont spread the event to the focus library
      // as that will force the focus system to focus a nonexistent focus key.
      // There is a small rendering gap between the player controls and this overlay being rendered
      // where this problem can occur
      if (!doesFocusableExist(getCurrentFocusKey())) {
        event.stopPropagation();
      }
    },
    [onClose, registerKeyDown]
  );

  useEventListener('keydown', handleKeyDown, undefined, true);
  useEventListener('keyup', registerKeyUp, undefined, true);

  const handleSelectSubtitleTrack = useCallback(
    (id: string) => {
      setSelectedSubtitleTrack(subtitleTracks?.find((subtitle) => subtitle.id === id));

      player?.selectSubtitleTrack(id === NO_SUBTITLE_TRACK_ID ? '' : id);

      onClose();
    },
    [subtitleTracks, player, setSelectedSubtitleTrack, onClose]
  );

  const buttonGroupSubtitles = useMemo(() => {
    const data = [
      {
        label: 'Fra',
        id: NO_SUBTITLE_TRACK_ID,
        selected: !selectedSubtitleTrack,
        onEnterPress: () => handleSelectSubtitleTrack(NO_SUBTITLE_TRACK_ID)
      },
      ...(subtitleTracks?.map((subtitleTrack) => ({
        label: getSubtitleTrackDisplayText(subtitleTrack),
        id: subtitleTrack.id,
        selected: subtitleTrack.id === (selectedSubtitleTrack?.id ?? NO_SUBTITLE_TRACK_ID),
        onEnterPress: () => handleSelectSubtitleTrack(subtitleTrack.id),
        dataTestId: testIds.player.subtitlesDialog.button
      })) || [])
    ];
    return data;
  }, [subtitleTracks, selectedSubtitleTrack, handleSelectSubtitleTrack]);

  const buttonGroupAudioTracks = useMemo(() => {
    // Filter out 'ukendt' audio tracks
    const filteredAudioTracks = audioTracks?.filter(
      (track) => !cleanAudioTrackText(track.id).includes('und_undefined')
    );

    const data = filteredAudioTracks?.map((audioTrack) => ({
      label: getAudioTrackDisplayText(audioTrack),
      id: audioTrack.id,
      selected: cleanAudioTrackText(audioTrack.id) === cleanAudioTrackText(selectedAudioTrack?.id),
      onEnterPress: () => handleSelectAudioTrack(cleanAudioTrackText(audioTrack.id)),
      dataTestId: testIds.player.audioDialog.button
    }));
    return data;
  }, [audioTracks, selectedAudioTrack, handleSelectAudioTrack]);

  // Filter out 'ukendt' audio tracks for the audioFound check
  const filteredAudioTracks = useMemo(() => {
    return audioTracks?.filter((track) => !cleanAudioTrackText(track.id).includes('und_undefined'));
  }, [audioTracks]);

  const audioFound = !!filteredAudioTracks?.length;
  const subtitleFound = !!subtitleTracks?.length;

  const initialFocusedAudioIndex = audioFound
    ? Math.max(buttonGroupAudioTracks?.findIndex((button) => button.selected) ?? -1, 0)
    : undefined;
  const initialFocusedSubtitleIndex =
    !audioFound && subtitleFound
      ? Math.max(
          buttonGroupSubtitles.findIndex((button) => button.selected),
          0
        )
      : undefined;

  const refSubtitles = useRef(null);
  const refAudio = useRef(null);

  const handleOnNavFocus = useCallback(
    (ref: React.RefObject<HTMLElement>, { node }: { node: HTMLElement }) => {
      if (ref.current !== null) {
        // Use the node's own offsetLeft (stable) rather than the scroll-relative x from the layout,
        // which would compound drift across successive focus moves.
        ref.current.scrollTo({
          left: node.offsetLeft - 550,
          behavior: 'instant'
        });
      }
    },
    []
  );

  const handleOnNavFocusSubtitles = useCallback(
    ({ node }: { node: HTMLElement }) => {
      handleOnNavFocus(refSubtitles, { node });
    },
    [handleOnNavFocus, refSubtitles]
  );

  const handleOnNavFocusAudio = useCallback(
    ({ node }: { node: HTMLElement }) => {
      handleOnNavFocus(refAudio, { node });
    },
    [handleOnNavFocus, refAudio]
  );

  return (
    <Overlay
      open={open}
      onClose={onClose}
      onBackdropClick={IS_BROWSER ? onClose : undefined}
      overlayContainerProps={{ 'data-testid': testIds.player.settingsOverlay.dialog }}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      <div>
        <Typography variant="h2" data-testid={testIds.player.settingsOverlay.title}>
          Lyd og Undertekster
        </Typography>
        <Typography
          variant="caption2"
          style={{ opacity: 0.6, marginBottom: '16px' }}
          data-testid={testIds.player.subtitlesDialog.label}
          component="div"
        >
          Bruges hvis tilgængelig
        </Typography>
        <div className={styles['labelled-button-group']}>
          <Typography
            id="audio-track-group-label"
            variant="h6"
            style={{ opacity: audioFound ? '1' : '0.4' }}
          >
            Lydspor
          </Typography>
          {audioFound && (
            <div onClick={(e) => e.stopPropagation()}>
              <ButtonGroup
                buttons={buttonGroupAudioTracks}
                ref={refAudio}
                onNavFocus={handleOnNavFocusAudio}
                dataTestId={testIds.player.audioDialog.buttonGroup}
                ariaLabelledBy="audio-track-group-label"
                focusedIndex={initialFocusedAudioIndex}
              />
            </div>
          )}
        </div>
        <div className={styles['labelled-button-group']}>
          <Typography
            id="subtitle-track-group-label"
            variant="h6"
            style={{ opacity: subtitleFound ? '1' : '0.4' }}
          >
            Undertekster
          </Typography>
          {subtitleFound && (
            <div onClick={(e) => e.stopPropagation()}>
              <ButtonGroup
                buttons={buttonGroupSubtitles}
                ref={refSubtitles}
                onNavFocus={handleOnNavFocusSubtitles}
                dataTestId={testIds.player.subtitlesDialog.buttonGroup}
                ariaLabelledBy="subtitle-track-group-label"
                focusedIndex={initialFocusedSubtitleIndex}
              />
            </div>
          )}
        </div>
      </div>
    </Overlay>
  );
};

export default React.memo(PlayerSettingsOverlay);
